/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import {
  createTrustRelationshipUrl,
  deleteTrustRelationshipUrl,
  getTrustedEnvsUrl,
  getTrustRelationshipsUrl,
  getTrustRelationshipUrl,
  updateTrustRelationshipUrl,
} from '@modules/cloud-api/v1/urls'
import {
  ElasticsearchClusterTrustSettings,
  TrustRelationshipCreateResponse,
  TrustRelationshipGetResponse,
} from '@modules/cloud-api/v1/types'
import type {
  TrustRelationshipCreateRequest,
  TrustRelationshipUpdateRequest,
} from '@modules/cloud-api/v1/types'

import {
  CREATE_TRUST_RELATIONSHIP,
  DELETE_TRUST_RELATIONSHIP,
  FETCH_TRUST_RELATIONSHIP,
  FETCH_TRUST_RELATIONSHIPS,
  FETCH_TRUSTED_ENVS,
  UPDATE_TRUST_RELATIONSHIP,
} from '@/constants/actions'

import asyncRequest, { resetAsyncRequest } from '../asyncRequests'

import type { Action } from '@/types/redux'
import type { RESET_ASYNC_REQUEST } from '@/constants/actions'

export function fetchTrustRelationships({
  regionId,
  includeCertificate = false,
}: {
  regionId: string
  includeCertificate?: boolean
}) {
  const url = getTrustRelationshipsUrl({
    regionId,
    includeCertificate,
  })

  return asyncRequest({
    type: FETCH_TRUST_RELATIONSHIPS,
    method: `GET`,
    url,
    meta: { regionId },
    crumbs: [regionId],
  })
}

export function fetchTrustRelationship({
  regionId,
  trustRelationshipId,
  includeCertificate,
}: {
  regionId: string
  trustRelationshipId: string
  includeCertificate: boolean
}) {
  const url = getTrustRelationshipUrl({
    regionId,
    trustRelationshipId,
    includeCertificate,
  })

  return asyncRequest<typeof FETCH_TRUST_RELATIONSHIP, TrustRelationshipGetResponse>({
    type: FETCH_TRUST_RELATIONSHIP,
    method: `GET`,
    url,
    meta: { regionId, trustRelationshipId },
    crumbs: [regionId, trustRelationshipId],
  })
}

export function fetchTrustedEnvs() {
  const url = getTrustedEnvsUrl()

  return asyncRequest<typeof FETCH_TRUSTED_ENVS, ElasticsearchClusterTrustSettings>({
    type: FETCH_TRUSTED_ENVS,
    method: `GET`,
    url,
  })
}

export function createTrustRelationship({
  regionId,
  payload,
}: {
  regionId: string
  payload: TrustRelationshipCreateRequest
}) {
  const url = createTrustRelationshipUrl({
    regionId,
  })

  return asyncRequest<typeof CREATE_TRUST_RELATIONSHIP, TrustRelationshipCreateResponse>({
    type: CREATE_TRUST_RELATIONSHIP,
    method: `POST`,
    url,
    payload,
    meta: { regionId },
    crumbs: [regionId],
  })
}

export function deleteTrustRelationship({
  regionId,
  trustRelationshipId,
  version,
}: {
  regionId: string
  trustRelationshipId: string
  version?: number | null
}) {
  const url = deleteTrustRelationshipUrl({
    regionId,
    trustRelationshipId,
    version,
  })

  return asyncRequest({
    type: DELETE_TRUST_RELATIONSHIP,
    method: `DELETE`,
    url,
    meta: { regionId, trustRelationshipId },
    crumbs: [regionId, trustRelationshipId],
  })
}

export function updateTrustRelationship({
  regionId,
  trustRelationshipId,
  payload,
}: {
  regionId: string
  trustRelationshipId: string
  payload: TrustRelationshipUpdateRequest
}) {
  const url = updateTrustRelationshipUrl({
    regionId,
    trustRelationshipId,
  })

  return asyncRequest({
    type: UPDATE_TRUST_RELATIONSHIP,
    method: `PUT`,
    url,
    payload,
    meta: { regionId, trustRelationshipId },
    crumbs: [regionId, trustRelationshipId],
  })
}

export const resetFetchTrustRelationships = (
  ...crumbs: string[]
): Action<typeof RESET_ASYNC_REQUEST> => resetAsyncRequest(FETCH_TRUST_RELATIONSHIPS, crumbs)

export const resetCreateTrustRelationship = (
  ...crumbs: string[]
): Action<typeof RESET_ASYNC_REQUEST> => resetAsyncRequest(CREATE_TRUST_RELATIONSHIP, crumbs)

export const resetUpdateTrustRelationship = (
  regionId: string,
  trustRelationshipId: string,
): Action<typeof RESET_ASYNC_REQUEST> =>
  resetAsyncRequest(UPDATE_TRUST_RELATIONSHIP, [regionId, trustRelationshipId])
